import { Machine, MachineOptions, EventObject, assign } from 'xstate'

export type ContentItemData = {
  id: string
  [any: string]: any
}

export type RemoveContentItemData = string //id
export type UpdateContentItemData = {
  id: string
  updateItem: (item: ContentItemData) => Partial<ContentItemData>
}
export interface LoadContentMachineEvent extends EventObject {
  type: 'LOAD'
  data: ContentItemData[]
}

export interface RemoveContentMachineEvent extends EventObject {
  type: 'REMOVE'
  data: RemoveContentItemData[]
}

export interface UpdateContentMachineEvent extends EventObject {
  type: 'UPDATE'
  data: UpdateContentItemData[]
}
export type ContentMachineEvents =
  | LoadContentMachineEvent
  | RemoveContentMachineEvent
  | UpdateContentMachineEvent

export interface ContentMachineContext {
  content: { [resourceID: string]: ContentItemData }
  lastEvent: {
    type: 'LOAD' | 'REMOVE' | 'UPDATE' | ''
    data: ContentItemData[] | RemoveContentItemData[] | UpdateContentItemData[]
  }
}

export interface ContentMachineStates {
  states: {
    ready: {}
    loadingContent: {}
    removingContent: {}
    updatingContent: {}
  }
}

const contentMachineOptions: Partial<
  MachineOptions<ContentMachineContext, ContentMachineEvents>
> = {
  actions: {
    makeCurrentEvent: assign((ctx, evt) => ({
      lastEvent: {
        type: evt.type,
        data:
          evt.type === 'UPDATE'
            ? evt.data.map((data) => {
                const item = { ...ctx.content[data.id], id: data.id }
                return { ...item, ...data.updateItem(item), id: item.id }
              })
            : evt.data,
      },
    })),
    loadContent: assign((ctx: any, evt: any) => {
      const content = { ...ctx.content }
      evt.data.forEach((data: ContentItemData) => {
        content[data.id] = { ...content[data.id], ...data }
      })
      return {
        content,
      }
    }),
    updateContent: assign((ctx: any, evt: any) => {
      const content = { ...ctx.content }
      evt.data.forEach((data: UpdateContentItemData) => {
        const item = { id: data.id, ...content[data.id] }
        content[data.id] = { ...item, ...data.updateItem(item), id: item.id }
      })
      return {
        content,
      }
    }),
    removeContent: assign((ctx: any, evt: any) => {
      const content = { ...ctx.content }
      evt.data.forEach((id: string) => {
        delete content[id]
      })
      return {
        content,
      }
    }),
  },
}

export const contentMachine = Machine<
  ContentMachineContext,
  ContentMachineStates,
  ContentMachineEvents
>(
  {
    id: 'ContentMachine',
    initial: 'ready',
    context: {
      content: {},
      lastEvent: {
        type: '',
        data: [],
      },
    },
    states: {
      ready: {},
      loadingContent: {
        after: {
          100: {
            target: 'ready',
          },
        },
      },
      removingContent: {
        after: {
          100: {
            target: 'ready',
          },
        },
      },
      updatingContent: {
        after: {
          100: {
            target: 'ready',
          },
        },
      },
    },
    on: {
      LOAD: {
        target: 'loadingContent',
        actions: ['makeCurrentEvent', 'loadContent'],
      },
      REMOVE: {
        target: 'removingContent',
        actions: ['makeCurrentEvent', 'removeContent'],
      },
      UPDATE: {
        target: 'updatingContent',
        actions: ['makeCurrentEvent', 'updateContent'],
      },
    },
  },
  contentMachineOptions
)
